import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Enables Ctrl/Cmd + click to open internal links in new tabs globally.
 * Handles same-origin absolute URLs (https://yourdomain/...) as internal.
 * Automatically ignores programmatic or non-user-triggered link clicks.
 */
export const useGlobalSmartNavigation = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleClick = (e) => {
      // 🚫 Ignore clicks already handled by other components (e.g., Link component)
      if (e.defaultPrevented) return;

      const link = e.target.closest("a[href]");
      if (!link) return;

      // 🚫 Ignore clicks not triggered by actual user interaction
      // (programmatic link.click() calls, synthetic events, etc.)
      if (!e.isTrusted || e.clientX === 0 && e.clientY === 0) return;

      const href = link.getAttribute("href");
      if (!href) return;

      const isExternal =
        href.startsWith("http") && !href.startsWith(window.location.origin);

      // Ignore external, mailto, or hash links
      if (href.startsWith("#") || href.startsWith("mailto:") || isExternal) {
        return;
      }

      // Respect right-click / middle-click
      if (e.button !== 0) return;

      // Ctrl (Win/Linux) or Cmd (Mac) → open in new tab
      if (e.ctrlKey || e.metaKey) {
        window.open(href, "_blank", "noopener,noreferrer");
        e.preventDefault();
        return;
      }

      // Normal left-click → internal navigation
      e.preventDefault();

      const internalPath = href.startsWith(window.location.origin)
        ? href.replace(window.location.origin, "")
        : href;

      navigate(internalPath);
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [navigate]);
};